// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web — features/ponto/pontoApi.ts
//
//  Tipos e chamadas compartilhados do módulo de Ponto (RH) usados pelos
//  comboboxes e pelas telas: pesquisa de funcionários, jornadas, cargos,
//  departamentos e lookup de usuários de login. Tudo passa pelo proxy
//  /api/rh/ponto (o EmpresaId vem do token).
// ════════════════════════════════════════════════════════════════════════════

import { apiClient } from "@/lib/apiClient";

export interface FuncionarioPonto {
  id: number;
  nome: string;
  sCnpjCpf: string;
  sMatricula: string;
  idJornada: number;
  jornadaNome: string;
  jornadaInativa: boolean;
  idUsuarioLogin: number;
  idCargo: number;
  idDepartamento: number;
  sCargo: string;
  sDepartamento: string;
  sPis: string;
  sLocal: string;
  dtAdmissao: string | null;
  dtDemissao: string | null;
  dtNascimento: string | null;
  sRg: string;
  sSexo: string;
  sTelefone: string;
  sRegime: string;
  ibDispensadoPonto: boolean;
  ibAtivo: boolean;
  cpfPendente: boolean;
  menorDeIdade: boolean;
}

export interface Paginado<T> {
  itens: T[];
  total: number;
  pagina: number;
  tamanhoPagina: number;
}

export type SituacaoFuncionario = "ativos" | "inativos" | "todos" | "cpf-pendente";

export interface FiltroFuncionarios {
  busca?: string;
  /** undefined = qualquer; 0 = sem departamento. */
  departamentoId?: number;
  cargoId?: number;
  jornadaId?: number;
  situacao?: SituacaoFuncionario;
  ids?: number[];
  ordenarPor?: "nome" | "matricula" | "cpf" | "cargo" | "departamento" | "jornada";
  direcao?: "asc" | "desc";
  pagina?: number;
  tamanhoPagina?: number;
}

export interface JornadaPonto {
  id: number;
  sNome: string;
  iTipo: number;
  fCargaSemanalMinutos: number;
  ibAtivo: boolean;
}

export interface DepartamentoPonto {
  id: number;
  sNome: string;
  sCodigo: string;
  sSigla: string;
  ibAtivo: boolean;
  qtdFuncionarios: number;
}

export interface CargoPonto {
  id: number;
  sNome: string;
  sCbo: string;
  idDepartamento: number;
  departamentoNome: string;
  fSalarioBase: number | null;
  ibAtivo: boolean;
  qtdFuncionarios: number;
}

export interface UsuarioLookup {
  id: number;
  nome: string;
  login: string;
  email: string | null;
  isAtivo: boolean;
}

const BASE = "/api/rh/ponto";

export const pontoApi = {
  pesquisarFuncionarios: (f: FiltroFuncionarios) =>
    apiClient.get<Paginado<FuncionarioPonto>>(`${BASE}/funcionarios/pesquisa`, {
      params: { ...f, ids: f.ids?.length ? f.ids.join(",") : undefined },
    }).then((r) => r.data),

  jornadas: (somenteAtivas = false) =>
    apiClient.get<JornadaPonto[]>(`${BASE}/jornadas`, { params: { somenteAtivas } }).then((r) => r.data),

  departamentos: (somenteAtivos = false) =>
    apiClient.get<DepartamentoPonto[]>(`${BASE}/departamentos`, { params: { somenteAtivos } }).then((r) => r.data),

  cargos: (somenteAtivos = false) =>
    apiClient.get<CargoPonto[]>(`${BASE}/cargos`, { params: { somenteAtivos } }).then((r) => r.data),

  usuarios: (busca?: string, ids?: number[]) =>
    apiClient.get<UsuarioLookup[]>(`${BASE}/usuarios-lookup`, {
      params: { busca: busca || undefined, ids: ids?.length ? ids.join(",") : undefined },
    }).then((r) => r.data),
};

/** Rótulo padrão de funcionário no Ponto: "Matrícula — Nome" (só o nome se não tiver matrícula). */
export const rotuloFuncionario = (f: Pick<FuncionarioPonto, "nome" | "sMatricula">) =>
  f.sMatricula ? `${f.sMatricula} — ${f.nome}` : f.nome;

/** CPF só com dígitos → 000.000.000-00 (outros tamanhos ficam como vieram). */
export const formatarCpf = (cpf: string) => {
  const d = (cpf ?? "").replace(/\D/g, "");
  return d.length === 11 ? `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}` : cpf;
};

/** minutos → "±HH:MM" */
export function hm(min: number): string {
  const s = min < 0 ? "-" : "";
  const a = Math.abs(min);
  return `${s}${String(Math.floor(a / 60)).padStart(2, "0")}:${String(a % 60).padStart(2, "0")}`;
}
